/**
 * Routes that are built but NOT ready to be indexed.
 *
 * ── HOW TO LAUNCH A PAGE ──────────────────────────────────────────────────
 *   1. Delete its line from IN_DEVELOPMENT_ROUTES below.
 *   2. `node scripts/sync-sitemap-routes.mjs --add <route>`
 *   3. `npm run sitemap && npm run build`
 *   4. Search Console → URL Inspection → Request Indexing.
 *
 * Step 4 matters. Google only learns the noindex is gone when it next crawls,
 * and its own docs warn that "it may take months for Googlebot to revisit a
 * page" on its own. Requesting indexing turns that into days.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * This list is the single source of truth. src/component/SEO.jsx reads it and
 * forces `noindex, follow` on any page whose url matches — a page cannot opt
 * out by passing its own `robots` prop, which is deliberate: the failure mode
 * we care about is someone launching a page and forgetting to un-noindex it,
 * so removal has to happen in exactly one place rather than across 13 files.
 *
 * `scripts/sync-sitemap-routes.mjs` fails if any route here is also listed in
 * sitemap-routes.json — a URL that is both noindexed and in the sitemap is a
 * contradiction Search Console reports as an error.
 *
 * Do NOT add permanently-noindexed pages here (thank-you pages, form steps,
 * A/B variants). Those set their own meta robots on the page and are not
 * waiting to launch. This list is only for work in progress.
 */

/** Robots value used for everything in this list. */
export const IN_DEVELOPMENT_ROBOTS = "noindex, follow";

/**
 * `follow` rather than `nofollow` on purpose: these pages link out to live
 * /services/* and /portfolio/* pages, and there is no reason to dead-end that
 * link value while they wait to launch.
 */
export const IN_DEVELOPMENT_ROUTES = new Set([
    "/new-services",
    "/new-services/shopify-development",
    "/new-services/shopify-plus-development",
    "/new-services/shopify-migration",
    "/new-services/headless-commerce",
    "/new-services/cro",
    "/new-services/store-performance",
    "/new-services/graphic-design",
    "/new-services/packaging-design",
    "/new-services/ui-ux",
    "/new-services/visual-identity",
    "/new-services/web-design",
    "/new-services/ab-testing",
]);

/**
 * Accepts a pathname ("/new-services/cro") or a full URL
 * ("https://abox.agency/new-services/cro"), since pages hold the latter.
 */
export function isInDevelopment(urlOrPath) {
    if (!urlOrPath) return false;

    let pathname = String(urlOrPath);
    if (pathname.startsWith("http")) {
        try {
            pathname = new URL(pathname).pathname;
        } catch {
            return false;
        }
    }

    // Tolerate a trailing slash so "/new-services/cro/" still matches.
    if (pathname.length > 1) pathname = pathname.replace(/\/$/, "");

    return IN_DEVELOPMENT_ROUTES.has(pathname);
}
